import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppContact, AppIndex } from "./AppIndex";
import { AppTaskAdd } from "./AppTaskApi";
import { AppTaskBoard } from "./AppTaskBoard";
import { AppTaskView } from "./AppTaskView";

// AppPhoneTaskAdd - "Add task" for THE OPEN CHAT on a phone (owner review 2026-11-01: "the add-task area uses too much height;
// a generic 'add a task for anyone' is not needed on a phone"). The Tasks tab's generic new-task row is hidden below 1100px
// (`app.css` `wac-review2-work`); the phone chat header's "Add task" button (`AppPhoneChat`) opens this native <dialog> with the
// same fields minus the contact picker - the contact is the open chat. The add goes through `AppTaskBoard.Add` (the one adder:
// the page-minted `u-` id, `/tasks add`, the optimistic row). ⛔ Nothing here sends a WhatsApp message.

export interface IPhoneTaskField {
    value: string;
}

export interface IPhoneTaskForm {
    querySelector(Selector: string): IPhoneTaskField | null;
}

export class AppPhoneTaskAdd {
    public static readonly DialogId: string = "wac-taddchat";
    public static readonly FormId: string = "wac-taddc";
    public static readonly SayId: string = "wac-taddc-say";

    /** The dialog's markup: a heading naming the contact, the fields, a Cancel and the form's Add. */
    public static Html = (Index: AppIndex, Who: AppContact): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<dialog id=\"" + AppPhoneTaskAdd.DialogId + "\" class=\"wac-taddchat\" aria-labelledby=\"" + AppPhoneTaskAdd.DialogId + "-h\">"
                + "<h2 id=\"" + AppPhoneTaskAdd.DialogId + "-h\">Add a task with " + kHTMLCore.EscapeHtml(Who.Title) + "</h2>"
                + AppTaskView.AddForm(Index, Who.Href, AppPhoneTaskAdd.FormId)
                + "<p class=\"tsay\" id=\"" + AppPhoneTaskAdd.SayId + "\" role=\"status\" aria-live=\"polite\"></p>"
                + "<button type=\"button\" class=\"btn secondary sm\" data-wac-taddclose=\"1\">Cancel</button></dialog>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskAdd.Html");
            return "";
        }
    };

    /** The typed task, read from the dialog's form (the same field suffixes as the Tasks tab's row). */
    public static Read = (Form: IPhoneTaskForm): AppTaskAdd => {
        const Out: AppTaskAdd = new AppTaskAdd();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.Label = AppPhoneTaskAdd.Field(Form, "-l").trim();
            Out.Value = AppPhoneTaskAdd.Field(Form, "-v").trim();
            Out.Due = AppPhoneTaskAdd.Field(Form, "-d").trim();
            Out.Owner = AppPhoneTaskAdd.Field(Form, "-o");
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskAdd.Read");
            return Out;
        }
    };

    /** The contact the dialog is for (its hidden field), or "". */
    public static HrefOf = (Form: IPhoneTaskForm): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppPhoneTaskAdd.Field(Form, "-c");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskAdd.HrefOf");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: one lookup; the callers guard. */
    private static Field = (Form: IPhoneTaskForm, Suffix: string): string => {
        const Box: IPhoneTaskField | null = Form.querySelector("#" + AppPhoneTaskAdd.FormId + Suffix);
        if (Box === null) { return ""; }
        return nz(Box.value);
    };

    /**
     * Validates and adds through the board: "" when it went (the dialog closes), else the ink sentence to show.
     * ⚠ Validation is `AppTaskBoard.AddRefusal` - the Tasks tab's own rule.
     */
    public static Submit = (Form: IPhoneTaskForm, Index: AppIndex, Board: AppTaskBoard): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Add: AppTaskAdd = AppPhoneTaskAdd.Read(Form);
            const Who: AppContact | null = AppTaskBoard.ContactOf(Index, AppPhoneTaskAdd.HrefOf(Form));
            const Why: string = AppTaskBoard.AddRefusal(Add, Who, Board.Today());
            if (Why.length > 0) { return Why; }
            Board.Add(Add, Who as AppContact);
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskAdd.Submit");
            return "";
        }
    };
}

/** The browser half: one dialog at a time, made for the open chat, removed when it closes. */
export class AppPhoneTaskAddRun {
    private Index: AppIndex;
    private Board: AppTaskBoard;
    private Box: HTMLDialogElement | null = null;

    public constructor(Index: AppIndex, Board: AppTaskBoard) {
        this.Index = Index;
        this.Board = Board;
    }

    public Open = (Href: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Who: AppContact | null = AppTaskBoard.ContactOf(this.Index, Href);
            if (Who === null) { return; }
            const Old: HTMLElement | null = document.getElementById(AppPhoneTaskAdd.DialogId);
            if (Old !== null) { Old.remove(); }
            const Holder: HTMLElement = document.createElement("div");
            Holder.innerHTML = AppPhoneTaskAdd.Html(this.Index, Who);
            const Box: HTMLDialogElement | null = Holder.querySelector("dialog");
            if (Box === null) { return; }
            document.body.appendChild(Box);
            this.Box = Box;
            Box.addEventListener("submit", this.OnSubmit);
            Box.addEventListener("click", this.OnClick);
            Box.addEventListener("close", this.OnClose);
            Box.showModal();
            const First: HTMLElement | null = Box.querySelector("#" + AppPhoneTaskAdd.FormId + "-l");
            if (First !== null) { First.focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskAddRun.Open");
        }
    };

    private OnSubmit = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Ev.preventDefault();
            if (this.Box === null) { return; }
            const Why: string = AppPhoneTaskAdd.Submit(this.Box as unknown as IPhoneTaskForm, this.Index, this.Board);
            if (Why.length > 0) {
                const Say: HTMLElement | null = this.Box.querySelector("#" + AppPhoneTaskAdd.SayId);
                if (Say !== null) { Say.textContent = Why; }
                return;
            }
            this.Box.close();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskAddRun.OnSubmit");
        }
    };

    private OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Box === null || !(Ev.target instanceof Element)) { return; }
            if (Ev.target === this.Box || Ev.target.closest("[data-wac-taddclose]") !== null) { this.Box.close(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskAddRun.OnClick");
        }
    };

    private OnClose = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Box !== null) { this.Box.remove(); }
            this.Box = null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPhoneTaskAddRun.OnClose");
        }
    };
}
